import { useEffect, useMemo, useState, type ReactNode, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  createUser, getUser, updateUser, listUsers,
  type CreateUserInput, type UpdateUserInput, type User,
} from '../../api/usersApi.js';
import { listRoles, type Role } from '../../api/rolesApi.js';
import { listCompaniesLite, type CompanyLite } from '../../api/orgApi.js';
import { listVendors, listVendorsWithLogin, type VendorSummary, type VendorWithLogin } from '../../api/vendorsApi.js';
import { useTenantIdentityState } from '../../shell/useTenantIdentity.js';
import { Button } from '../../../../shared/components/index.js';
import { UserGeographyPanel } from './UserGeographyPanel.js';
import { UserRolesPanel } from './UserRolesPanel.js';
import { UserAccountActionsPanel } from './UserAccountActionsPanel.js';
import { DateInput, validateDateSubmission } from '../../../../shared/components/DateInput.js';

/** Backend expects E.164; the UI supplies +91 so operators type 10 digits. */
const TEN_DIGITS = /^[0-9]{10}$/;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const LOGIN_SUFFIX = /^[A-Za-z0-9_]{2,40}$/;

/**
 * The role's category decides who the person is, so the user type is derived
 * rather than asked twice — picking "Vendor" and leaving the type on INTERNAL
 * was exactly what the old single-form screen allowed.
 */
const CATEGORY_TO_USER_TYPE: Readonly<Record<string, string>> = {
  TENANT_ADMIN: 'INTERNAL',
  TENANT_STAFF: 'INTERNAL',
  PLATFORM_ADMIN: 'INTERNAL',
  VENDOR: 'VENDOR',
  WORKFORCE: 'PAINTER',
  AUDITOR: 'AUDITOR',
};

// t014_user_type_chk only accepts PAINTER, so the stored code stays as-is and
// only the wording shown to the operator says Field Force.
const USER_TYPE_LABEL: Readonly<Record<string, string>> = {
  INTERNAL: 'Internal',
  VENDOR: 'Vendor',
  PAINTER: 'Field Force',
  SUPPORT: 'Support',
  AUDITOR: 'Auditor',
};

function profileFor(category: string) {
  // Vendor creation lives on ONE page (/admin/vendors/new), which captures the
  // statutory, bank and KYC details a billable vendor needs. This screen creates
  // the LOGIN only and attaches it to a vendor that already exists.
  const isVendorFirm = category === 'VENDOR';
  const isFieldForce = category === 'WORKFORCE';
  return {
    showOrgFields: !isVendorFirm && !isFieldForce && category !== 'AUDITOR',
    requireVendor: isFieldForce || isVendorFirm,
  };
}

export function UserCreateWizardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const nav = useNavigate();
  const { identity: tenant, error: tenantError } = useTenantIdentityState(token);
  // One screen, two modes: /admin/users/new creates, /admin/users/:id/edit
  // edits the same fields, so an operator never meets a second, thinner form.
  const { id: editingUserId } = useParams<{ id: string }>();
  const isEdit = !!editingUserId;
  // Arriving from "Create login" on the user list, which names the vendor the
  // login is being issued for. That vendor is then fixed for this form.
  const [searchParams] = useSearchParams();
  const vendorIdParam = searchParams.get('vendorId');
  // Set when the user list sent us here because activation refused the profile.
  const activateError = (useLocation().state as { activateError?: string } | null)?.activateError;
  // Chosen on the entry screen, so the role is not asked for a second time.
  const roleIdParam = searchParams.get('roleId');

  const [roles, setRoles] = useState<readonly Role[]>([]);
  const [vendors, setVendors] = useState<readonly VendorSummary[]>([]);
  const [vendorsWithLogin, setVendorsWithLogin] = useState<readonly VendorWithLogin[]>([]);
  // Apart from `error`, which switching between create and edit resets.
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [roleId, setRoleId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [loginSuffix, setLoginSuffix] = useState('');
  const [existingLoginId, setExistingLoginId] = useState<string | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [department, setDepartment] = useState('');
  const [designation, setDesignation] = useState('');
  const [managerId, setManagerId] = useState('');
  const [managers, setManagers] = useState<readonly User[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState<readonly CompanyLite[]>([]);
  const [password, setPassword] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    // An empty picker with no reason reads as "there are none" — and the
    // vendor list below says exactly that — so a failed read is said.
    const failed = (e: unknown) => { if (alive) setLookupError((e as Error).message); };
    void listRoles(token)
      .then((r) => { if (alive) setRoles(r); })
      .catch(failed);
    void listVendors(token)
      .then((v) => { if (alive) setVendors(v); })
      .catch(failed);
    void listVendorsWithLogin(token)
      .then((v) => { if (alive) setVendorsWithLogin(v); })
      .catch(failed);
    // Candidates for the reporting line. A manager who cannot sign in cannot
    // act on anything, so only live accounts are offered.
    void listUsers({}, token)
      .then((r) => {
        if (alive) setManagers(r.data.filter((u) => u.userStatus === 'ACTIVE'));
      })
      .catch(failed);
    void listCompaniesLite(token)
      .then((r) => { if (alive) setCompanies(r.data); })
      .catch(failed);
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    let alive = true;
    // Both routes render this component, so React keeps the instance alive when
    // moving between "New user" and an edit. Without clearing first, the
    // previously edited person's values leak into the create form.
    setExistingLoginId(null);
    setVersion(null);
    setError(activateError ?? null);
    setRoleId(roleIdParam ?? '');
    setVendorId(vendorIdParam ?? '');
    setLoginSuffix('');
    setPassword('');
    setFirstName('');
    setMiddleName('');
    setLastName('');
    setDisplayName('');
    setMobile('');
    setEmail('');
    setDepartment('');
    setDesignation('');
    setManagerId('');
    setCompanyId('');
    setEffectiveFrom('');

    if (!editingUserId) return;
    setLoading(true);
    void getUser(editingUserId, token)
      .then((u) => {
        if (!alive) return;
        setExistingLoginId(u.employeeCode);
        setVersion(u.version);
        setRoleId(u.defaultRoleId ?? '');
        // Read-only here, but without it the vendor panel claims the login is
        // not linked to any firm.
        setVendorId(u.vendorId ?? '');
        setFirstName(u.firstName ?? '');
        setMiddleName(u.middleName ?? '');
        setLastName(u.lastName ?? '');
        setDisplayName(u.displayName ?? '');
        // Stored E.164; the field takes the 10 national digits.
        setMobile((u.mobileNumber ?? '').replace(/^\+91/, ''));
        setEmail(u.emailAddress ?? '');
        setDepartment(u.department ?? '');
        setDesignation(u.designation ?? '');
        setManagerId(u.managerId ?? '');
        setCompanyId(u.companyId ?? '');
        setEffectiveFrom((u.effectiveFromDate ?? '').slice(0, 10));
      })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [editingUserId, token, roleIdParam, vendorIdParam, activateError]);

  const selectedRole = useMemo(
    () => roles.find((r) => r.roleId === roleId) ?? null,
    [roles, roleId],
  );
  const category = selectedRole?.roleCategoryCode ?? '';
  const userType = CATEGORY_TO_USER_TYPE[category] ?? 'INTERNAL';
  const profile = profileFor(category);
  const prefix = tenant?.tenantCode ? `${tenant.tenantCode}_` : '';
  // A new login id is the tenant prefix plus the suffix; without the prefix
  // nothing may be created. An edit never touches the login id.
  const loginIdUnavailable = !isEdit && !prefix;

  /**
   * The tenant's own administrator, found by role category rather than by a
   * role code, so a tenant that names the role differently still resolves.
   */
  const tenantAdminUserId = useMemo(() => {
    const adminRoleIds = new Set(
      roles.filter((r) => r.roleCategoryCode === 'TENANT_ADMIN').map((r) => r.roleId),
    );
    return managers.find((m) => m.defaultRoleId && adminRoleIds.has(m.defaultRoleId))?.userId ?? '';
  }, [roles, managers]);

  // Staff are expected to report to someone; the administrator is offered as
  // the starting point so the line is never simply left blank by accident.
  // It stays a suggestion - the operator can pick anyone else, or nobody.
  useEffect(() => {
    if (isEdit || managerId || !tenantAdminUserId) return;
    if (category !== 'TENANT_STAFF') return;
    setManagerId(tenantAdminUserId);
  }, [isEdit, managerId, tenantAdminUserId, category]);

  /** The vendor this login is being issued for, when the list sent us here. */
  const lockedVendor = vendorIdParam
    ? vendorsWithLogin.find((v) => v.vendorId === vendorIdParam) ?? null
    : null;
  /** A vendor may hold at most one login; the list offers the button only when
   *  there is none, but the page is reachable directly. */
  const alreadyHasLogin = vendorsWithLogin.find((v) => v.vendorId === vendorId)?.loginUserId ?? null;

  // Seed the contact details the vendor was registered with, so the operator
  // confirms them rather than retyping them.
  useEffect(() => {
    if (isEdit || !lockedVendor) return;
    setFirstName((c) => c || (lockedVendor.contactPerson?.split(' ')[0] ?? ''));
    setLastName((c) => c || (lockedVendor.contactPerson?.split(' ').slice(1).join(' ') ?? ''));
    setEmail((c) => c || (lockedVendor.email ?? ''));
    setMobile((c) => c || (lockedVendor.mobileNumber ?? '').replace(/^\+91/, ''));
  }, [isEdit, lockedVendor]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (loginIdUnavailable) return;
    if (!roleId) { setError(t('users.create.err.role')); return; }
    if (!isEdit && !LOGIN_SUFFIX.test(loginSuffix.trim())) { setError(t('users.create.err.loginId')); return; }
    if (!firstName.trim()) { setError(t('users.create.err.firstName')); return; }
    if (!lastName.trim()) { setError(t('users.create.err.lastName')); return; }
    if (!EMAIL.test(email.trim())) { setError(t('users.create.err.email')); return; }
    if (!TEN_DIGITS.test(mobile.trim())) { setError(t('users.create.err.mobile')); return; }
    if (!isEdit && profile.requireVendor && !vendorId) { setError(t('users.create.err.vendor')); return; }
    // Checked at save as well as on the list, because the page can be reached
    // directly and a second login would silently orphan the first.
    if (!isEdit && vendorId && alreadyHasLogin) { setError(t('users.create.err.vendorHasLogin')); return; }
    if (!isEdit && password.length < 8) { setError(t('users.create.err.password')); return; }
    if (!isEdit && !effectiveFrom) { setError(t('users.create.err.effectiveFrom')); return; }

    const opt = (v: string) => (v.trim() ? v.trim() : undefined);

    setBusy(true);
    try {
      if (isEdit) {
        if (version == null) { setError(t('users.edit.err.notLoaded')); return; }
        // employeeCode and vendorId are deliberately absent: the login id and
        // the vendor a login belongs to are fixed once issued.
        const patch: UpdateUserInput = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          displayName: opt(displayName) ?? `${firstName.trim()} ${lastName.trim()}`,
          mobileNumber: `+91${mobile.trim()}`,
          emailAddress: email.trim(),
          defaultRoleId: roleId,
          version,
          ...(opt(middleName) ? { middleName: opt(middleName) } : {}),
          ...(profile.showOrgFields && opt(department) ? { department: opt(department) } : {}),
          ...(profile.showOrgFields && opt(designation) ? { designation: opt(designation) } : {}),
          ...(profile.showOrgFields && opt(managerId) ? { managerId: opt(managerId) } : {}),
          ...(profile.showOrgFields && companyId ? { companyId } : {}),
        };
        await updateUser(editingUserId as string, patch, token);
        nav(`/admin/users/${editingUserId}`);
        return;
      }

      const body: CreateUserInput = {
        employeeCode: `${prefix}${loginSuffix.trim()}`,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        mobileNumber: `+91${mobile.trim()}`,
        emailAddress: email.trim(),
        temporaryPassword: password,
        effectiveFromDate: effectiveFrom,
        userType,
        defaultRoleId: roleId,
        ...(opt(middleName) ? { middleName: opt(middleName) } : {}),
        ...(opt(displayName) ? { displayName: opt(displayName) } : {}),
        ...(profile.showOrgFields && opt(department) ? { department: opt(department) } : {}),
        ...(profile.showOrgFields && opt(designation) ? { designation: opt(designation) } : {}),
        ...(profile.showOrgFields && opt(managerId) ? { managerId: opt(managerId) } : {}),
        ...(profile.showOrgFields && companyId ? { companyId } : {}),
        ...(vendorId ? { vendorId } : {}),
      };

      const r = await createUser(body, token);
      nav(`/admin/users/${r.userId}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      aria-label={isEdit ? 'user-edit' : 'user-create'}
      // Scrolling the wheel over a focused <select> silently changes its value
      // in every browser. On this form that can switch a person's role without
      // anyone noticing until after save, so the field gives up focus instead.
      onWheelCapture={(e) => {
        const el = e.target as HTMLElement;
        if (el instanceof HTMLSelectElement && document.activeElement === el) el.blur();
      }}
    >
      <h1>{isEdit ? t('users.edit.title') : t('users.create.title')}</h1>
      {tenantError ? <p role="alert" data-testid="tenant-error">{tenantError}</p> : null}
      {lookupError ? <p role="alert" data-testid="lookup-error">{lookupError}</p> : null}
      {isEdit && loading ? <p>{t('common.loading')}</p> : null}
      {isEdit && existingLoginId ? (
        <p data-testid="edit-login-id">
          {t('users.create.loginId')}: <strong>{existingLoginId}</strong>{' '}
          <em>{t('users.edit.loginIdLocked')}</em>
        </p>
      ) : null}
      <form onSubmit={onSubmit} aria-label={isEdit ? 'user-edit-form' : 'user-create-form'} noValidate onSubmitCapture={validateDateSubmission}>
        <fieldset>
          <legend>{t('users.create.step1')}</legend>
          {!isEdit && roleIdParam ? (
            // Already chosen on the entry screen; asking again invites a
            // mismatch between the role picked there and the one saved here.
            <p data-testid="role-readonly">
              {t('users.create.role')}: <strong>{selectedRole?.roleName ?? roleIdParam}</strong>
            </p>
          ) : (
            <div className="tl-field-row">
              <label htmlFor="u-role">{isEdit ? t('users.edit.role') : t('users.create.role')}</label>
              <select
                id="u-role"
                value={roleId}
                // The vendor is fixed once a login exists, so only the create
                // path re-seeds it when the role changes.
                onChange={(e) => { setRoleId(e.target.value); if (!isEdit) setVendorId(vendorIdParam ?? ''); }}
              >
                <option value="">{t('users.create.rolePlaceholder')}</option>
                {roles.map((r) => (
                  <option key={r.roleId} value={r.roleId}>{r.roleName}</option>
                ))}
              </select>
            </div>
          )}
          {selectedRole ? (
            <p data-testid="derived-user-type">
              {t('users.create.derivedType')}:{' '}
              <strong>{USER_TYPE_LABEL[userType] ?? userType}</strong>
            </p>
          ) : null}
        </fieldset>

        {selectedRole ? (
          <>
            {profile.requireVendor ? (
              <fieldset>
                <legend>{t('users.create.vendorSection')}</legend>
                {isEdit ? (
                  <p data-testid="vendor-readonly">
                    {t('users.create.vendor')}:{' '}
                    <strong>
                      {vendors.find((v) => v.vendorId === vendorId)
                        ? `${vendors.find((v) => v.vendorId === vendorId)?.vendorCode} — ${vendors.find((v) => v.vendorId === vendorId)?.vendorName}`
                        : t('users.edit.vendorUnknown')}
                    </strong>{' '}
                    <em>{t('users.edit.vendorLocked')}</em>
                  </p>
                ) : lockedVendor ? (
                  <div data-testid="vendor-locked">
                    <p>
                      {t('users.create.vendor')}:{' '}
                      <strong>{lockedVendor.vendorCode} — {lockedVendor.vendorName}</strong>{' '}
                      <em>{t('users.edit.vendorLocked')}</em>
                    </p>
                    {lockedVendor.contactPerson ? (
                      <p data-testid="vendor-contact">
                        {t('users.create.vendorContact')}: {lockedVendor.contactPerson}
                      </p>
                    ) : null}
                    {alreadyHasLogin ? (
                      <p role="alert" data-testid="vendor-has-login">
                        {t('users.create.err.vendorHasLogin')}
                        {lockedVendor.loginEmployeeCode ? ` (${lockedVendor.loginEmployeeCode})` : ''}
                      </p>
                    ) : null}
                  </div>
                ) : vendors.length === 0 && !lookupError ? (
                  <p role="alert" data-testid="no-vendors">{t('users.create.noVendors')}</p>
                ) : (
                  <div className="tl-field-row">
                    <label htmlFor="u-vendor">{t('users.create.vendor')}</label>
                    <select id="u-vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                      <option value="">{t('users.create.vendorPlaceholder')}</option>
                      {vendors.map((v) => (
                        <option key={v.vendorId} value={v.vendorId}>
                          {v.vendorCode} — {v.vendorName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Creating a vendor from here lands on the ONE vendor creation
                    page, so a vendor is never captured by a second, thinner form. */}
                {!isEdit && !lockedVendor ? (
                  <Button
                    type="button"
                    variant="secondary"
                    data-testid="create-vendor-from-user"
                    onClick={() => nav('/admin/vendors/new')}
                  >
                    {t('users.create.createVendorLink')}
                  </Button>
                ) : null}
              </fieldset>
            ) : null}

            <fieldset>
              <legend>{t('users.create.identity')}</legend>
              <div className="tl-field-row">
                <label htmlFor="u-first">{t('users.create.firstName')}</label>
                <input id="u-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="tl-field-row">
                <label htmlFor="u-middle">{t('users.create.middleName')}</label>
                <input id="u-middle" value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
              </div>
              <div className="tl-field-row">
                <label htmlFor="u-last">{t('users.create.lastName')}</label>
                <input id="u-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
              <div className="tl-field-row">
                <label htmlFor="u-display">{t('users.create.displayName')}</label>
                <input id="u-display" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </div>
              <div className="tl-field-row">
                <label htmlFor="u-mobile">{t('users.create.mobile')}</label>
                <span>
                  <span aria-label="dial-code"><strong>+91</strong></span>{' '}
                  <input
                    id="u-mobile"
                    inputMode="numeric"
                    maxLength={10}
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                  />
                </span>
              </div>
              <div className="tl-field-row">
                <label htmlFor="u-email">{t('users.create.email')}</label>
                <input id="u-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </fieldset>

            {profile.showOrgFields ? (
              <fieldset>
                <legend>{t('users.create.organisation')}</legend>
                <div className="tl-field-row">
                  <label htmlFor="u-company">{t('users.create.company')}</label>
                  <select id="u-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="user-company">
                    <option value="">{t('users.create.noCompany')}</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.companyName}</option>
                    ))}
                  </select>
                </div>
                <div className="tl-field-row">
                  <label htmlFor="u-dept">{t('users.create.department')}</label>
                  <input id="u-dept" value={department} onChange={(e) => setDepartment(e.target.value)} />
                </div>
                <div className="tl-field-row">
                  <label htmlFor="u-desig">{t('users.create.designation')}</label>
                  <input id="u-desig" value={designation} onChange={(e) => setDesignation(e.target.value)} />
                </div>
                <div className="tl-field-row">
                  <label htmlFor="u-manager">{t('users.create.reportingManager')}</label>
                  <select
                    id="u-manager"
                    value={managerId}
                    onChange={(e) => setManagerId(e.target.value)}
                    data-testid="reporting-manager"
                  >
                    <option value="">{t('users.create.noReportingManager')}</option>
                    {managers
                      .filter((m) => m.userId !== editingUserId)
                      .map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.displayName}{m.employeeCode ? ` (${m.employeeCode})` : ''}
                        </option>
                      ))}
                  </select>
                </div>
              </fieldset>
            ) : null}

            <fieldset>
              <legend>{t('users.create.loginSection')}</legend>
              {isEdit ? (
                <p data-testid="login-id-readonly">
                  {t('users.create.loginId')}: <strong>{existingLoginId ?? '—'}</strong>{' '}
                  <em>{t('users.edit.loginIdLocked')}</em>
                </p>
              ) : (
                <>
                  <div className="tl-field-row">
                    <label htmlFor="u-login">{t('users.create.loginId')}</label>
                    <span>
                      <span aria-label="login-id-prefix"><strong>{prefix}</strong></span>
                      <input id="u-login" value={loginSuffix} onChange={(e) => setLoginSuffix(e.target.value)} />
                    </span>
                  </div>
                  {loginSuffix ? (
                    <p data-testid="login-id-preview">
                      {t('users.create.loginIdPreview')}: <strong>{prefix}{loginSuffix}</strong>
                    </p>
                  ) : null}
                  <div className="tl-field-row">
                    <label htmlFor="u-pass">{t('users.create.tempPassword')}</label>
                    <input id="u-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
                  </div>
                  <p>{t('users.create.passwordRules')}</p>
                  <p>{t('users.create.tempPasswordNote')}</p>
                </>
              )}
              <div className="tl-field-row">
                <label htmlFor="u-from">{t('users.create.effectiveFrom')}</label>
                <DateInput id="u-from" type="date" value={effectiveFrom} onValueChange={(nextValue) => setEffectiveFrom(nextValue)}
                  disabled={isEdit} />
              </div>
            </fieldset>
          </>
        ) : null}

        {error ? <p role="alert">{error}</p> : null}
        <Button type="submit" variant="primary" disabled={busy || !roleId || loginIdUnavailable}>
          {isEdit ? t('users.edit.submit') : t('users.create.submit')}
        </Button>
      </form>

      {/* Scopes belong to a user that already exists, so this only appears on edit. */}
      {isEdit && editingUserId ? (
        <>
          <UserAccountActionsPanel userId={editingUserId} />
          <UserRolesPanel userId={editingUserId} />
          <UserGeographyPanel userId={editingUserId} />
        </>
      ) : null}
    </main>
  );
}
